import type { CustomLayerInterface, Map as MlMap, CustomRenderMethodInput } from 'maplibre-gl';
import { CONTOUR_INK, MAX_MINUTES, RAMP, bandMinutesFor, hexToRgb, rampAsFloats } from '../lib/palette';
import type { DisplayMode, GridSpec } from '../lib/types';

/**
 * Draws the travel-time field as a single textured quad over Berlin.
 *
 * The field lives in an RG16F texture (r = minutes, g = reachability) and all
 * styling — colour ramp, isochrone banding, contour lines, opacity falloff —
 * happens in the fragment shader. Restyling therefore costs nothing but a
 * repaint, and the GPU's bilinear filtering smooths the grid for free.
 */

const VERT = `#version 300 es
uniform mat4 u_matrix;
// Scales the [0,1] mercator vertices into whatever space u_matrix expects.
uniform float u_posScale;
in vec2 a_pos;
in vec2 a_uv;
out vec2 v_uv;
void main() {
  v_uv = a_uv;
  gl_Position = u_matrix * vec4(a_pos * u_posScale, 0.0, 1.0);
}`;

const FRAG = `#version 300 es
precision highp float;

#define RAMP_N ${RAMP.length}

uniform sampler2D u_field;
uniform vec3 u_ramp[RAMP_N];
uniform vec3 u_contourInk;
uniform float u_bandMinutes;
uniform float u_maxMinutes;
uniform float u_opacity;
uniform float u_bands;
uniform float u_contours;

in vec2 v_uv;
out vec4 fragColor;

vec3 rampSmooth(float t) {
  float x = clamp(t, 0.0, 1.0) * float(RAMP_N - 1);
  float i = floor(x);
  int i0 = int(i);
  int i1 = min(i0 + 1, RAMP_N - 1);
  return mix(u_ramp[i0], u_ramp[i1], x - i);
}

void main() {
  vec2 sample0 = texture(u_field, v_uv).rg;
  float minutes = sample0.r;
  float reach = sample0.g;
  if (reach < 0.02) discard;

  float n = clamp(minutes / u_maxMinutes, 0.0, 1.0);
  // How many bands the chosen width gives over the same range: 7 at 10 minutes,
  // 14 at 5. Quantising the ramp rather than indexing it means one code path
  // for both, and at 10 minutes it lands exactly on the seven ramp colours.
  float nBands = max(1.0, floor(u_maxMinutes / u_bandMinutes + 0.5));
  float bandIdx = clamp(floor(minutes / u_bandMinutes), 0.0, nBands - 1.0);
  vec3 banded = rampSmooth(bandIdx / max(nBands - 1.0, 1.0));
  vec3 color = mix(rampSmooth(n), banded, u_bands);

  // Contour lines sit on band boundaries, in screen space via derivatives.
  float f = minutes / u_bandMinutes;
  float g = fract(f);
  float dist = min(g, 1.0 - g);
  float fw = fwidth(f);
  float line = 1.0 - smoothstep(0.0, max(fw, 1e-5) * 0.8, dist);
  // Suppress in the saturated tail (f is exactly integral there) and fade out
  // when zoomed far enough that one texel covers most of a band.
  line *= step(f, nBands - 0.001);
  line *= 1.0 - smoothstep(0.15, 0.5, fw);
  color = mix(color, u_contourInk, line * 0.8 * u_contours);

  // Well-connected areas stay solid; slow ones recede toward the basemap, so
  // the eye lands on the places that are actually worth considering.
  float alpha = u_opacity * mix(1.0, 0.3, n) * smoothstep(0.02, 0.6, reach);

  // The index covers a bounding box, not the city outline. Feather the border so
  // it reads as "coverage ends here" rather than as a drawing artefact.
  vec2 edge = min(v_uv, 1.0 - v_uv);
  alpha *= smoothstep(0.0, 0.012, min(edge.x, edge.y));
  fragColor = vec4(color * alpha, alpha);
}`;

function compile(gl: WebGL2RenderingContext, type: number, source: string): WebGLShader {
  const shader = gl.createShader(type)!;
  gl.shaderSource(shader, source);
  gl.compileShader(shader);
  if (!gl.getShaderParameter(shader, gl.COMPILE_STATUS)) {
    const log = gl.getShaderInfoLog(shader);
    gl.deleteShader(shader);
    throw new Error(`heatmap shader failed to compile: ${log}`);
  }
  return shader;
}

export interface HeatmapStyle {
  display: DisplayMode;
  contours: boolean;
  opacity: number;
}

export class HeatmapLayer implements CustomLayerInterface {
  readonly id = 'travel-time-heatmap';
  readonly type = 'custom' as const;
  readonly renderingMode = '2d' as const;

  private map: MlMap | null = null;
  private gl: WebGL2RenderingContext | null = null;
  private program: WebGLProgram | null = null;
  private vao: WebGLVertexArrayObject | null = null;
  private vbo: WebGLBuffer | null = null;
  private texture: WebGLTexture | null = null;
  private uniforms: Record<string, WebGLUniformLocation | null> = {};

  private grid: GridSpec;
  private style: HeatmapStyle = { display: 'bands10', contours: true, opacity: 0.82 };
  private hasField = false;
  private texWidth = 0;
  private texHeight = 0;
  private renderCount = 0;
  /** Set when a field arrives before the GL context exists. */
  private pending: { data: Uint16Array; width: number; height: number } | null = null;

  /** Snapshot for debugging from the console / smoke tests. */
  debugState() {
    return {
      hasField: this.hasField,
      pending: !!this.pending,
      texWidth: this.texWidth,
      texHeight: this.texHeight,
      renderCount: this.renderCount,
      hasProgram: !!this.program,
      hasGl: !!this.gl,
      style: this.style,
      quad: Array.from(this.quadVertices()),
    };
  }

  constructor(grid: GridSpec) {
    this.grid = grid;
  }

  onAdd(map: MlMap, gl: WebGL2RenderingContext | WebGLRenderingContext) {
    if (!(typeof WebGL2RenderingContext !== 'undefined' && gl instanceof WebGL2RenderingContext)) {
      throw new Error('Lowcation needs WebGL2 to draw the heatmap');
    }
    this.map = map;
    this.gl = gl;

    const program = gl.createProgram()!;
    const vs = compile(gl, gl.VERTEX_SHADER, VERT);
    const fs = compile(gl, gl.FRAGMENT_SHADER, FRAG);
    gl.attachShader(program, vs);
    gl.attachShader(program, fs);
    gl.linkProgram(program);
    if (!gl.getProgramParameter(program, gl.LINK_STATUS)) {
      throw new Error(`heatmap program failed to link: ${gl.getProgramInfoLog(program)}`);
    }
    gl.deleteShader(vs);
    gl.deleteShader(fs);
    this.program = program;

    for (const name of [
      'u_matrix',
      'u_posScale',
      'u_field',
      'u_ramp',
      'u_contourInk',
      'u_bandMinutes',
      'u_maxMinutes',
      'u_opacity',
      'u_bands',
      'u_contours',
    ]) {
      this.uniforms[name] = gl.getUniformLocation(program, name);
    }

    this.vao = gl.createVertexArray();
    this.vbo = gl.createBuffer();
    gl.bindVertexArray(this.vao);
    gl.bindBuffer(gl.ARRAY_BUFFER, this.vbo);
    gl.bufferData(gl.ARRAY_BUFFER, this.quadVertices(), gl.STATIC_DRAW);
    const aPos = gl.getAttribLocation(program, 'a_pos');
    const aUv = gl.getAttribLocation(program, 'a_uv');
    gl.enableVertexAttribArray(aPos);
    gl.vertexAttribPointer(aPos, 2, gl.FLOAT, false, 16, 0);
    gl.enableVertexAttribArray(aUv);
    gl.vertexAttribPointer(aUv, 2, gl.FLOAT, false, 16, 8);
    gl.bindVertexArray(null);

    this.texture = gl.createTexture();
    gl.bindTexture(gl.TEXTURE_2D, this.texture);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.LINEAR);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);
    gl.bindTexture(gl.TEXTURE_2D, null);

    if (this.pending) {
      const { data, width, height } = this.pending;
      this.pending = null;
      this.setField(data, width, height);
    }
  }

  onRemove() {
    const gl = this.gl;
    if (!gl) return;
    if (this.program) gl.deleteProgram(this.program);
    if (this.vbo) gl.deleteBuffer(this.vbo);
    if (this.vao) gl.deleteVertexArray(this.vao);
    if (this.texture) gl.deleteTexture(this.texture);
    this.program = this.vbo = this.vao = this.texture = null;
    this.gl = null;
    this.map = null;
  }

  /** Quad corners in mercator space, paired with texture coordinates. */
  private quadVertices(): Float32Array {
    const { minX, minY, maxX, maxY } = this.grid;
    // v = 0 is the first row of texture data, which is the northern edge (minY).
    return new Float32Array([
      minX, minY, 0, 0,
      maxX, minY, 1, 0,
      minX, maxY, 0, 1,
      maxX, maxY, 1, 1,
    ]);
  }

  setGrid(grid: GridSpec) {
    this.grid = grid;
    const gl = this.gl;
    if (gl && this.vbo) {
      gl.bindBuffer(gl.ARRAY_BUFFER, this.vbo);
      gl.bufferData(gl.ARRAY_BUFFER, this.quadVertices(), gl.STATIC_DRAW);
      gl.bindBuffer(gl.ARRAY_BUFFER, null);
    }
  }

  setField(data: Uint16Array, width: number, height: number) {
    const gl = this.gl;
    if (!gl || !this.texture) {
      this.pending = { data, width, height };
      return;
    }
    gl.bindTexture(gl.TEXTURE_2D, this.texture);
    gl.texImage2D(gl.TEXTURE_2D, 0, gl.RG16F, width, height, 0, gl.RG, gl.HALF_FLOAT, data);
    const err = gl.getError();
    gl.bindTexture(gl.TEXTURE_2D, null);
    if (err !== gl.NO_ERROR) {
      console.error(`heatmap texture upload failed (gl error 0x${err.toString(16)})`);
      return;
    }
    this.texWidth = width;
    this.texHeight = height;
    this.hasField = true;
    this.map?.triggerRepaint();
  }

  clearField() {
    this.hasField = false;
    this.pending = null;
    this.map?.triggerRepaint();
  }

  setStyle(style: Partial<HeatmapStyle>) {
    this.style = { ...this.style, ...style };
    this.map?.triggerRepaint();
  }

  render(gl: WebGL2RenderingContext | WebGLRenderingContext, options: CustomRenderMethodInput) {
    if (!this.hasField || !this.program || !this.vao) return;
    this.renderCount++;
    const gl2 = gl as WebGL2RenderingContext;

    /*
     * Coordinate space, established by measurement against MapLibre 5.24:
     *  - defaultProjectionData.mainMatrix consumes [0,1] mercator directly and
     *    follows the active projection, so it is the one to use.
     *  - modelViewProjectionMatrix consumes mercator * worldSize (i.e. world
     *    pixels at the current zoom), so it needs rescaling every frame.
     * The upstream "Null Island" doc example predates this and is misleading.
     */
    const main = options.defaultProjectionData?.mainMatrix;
    const matrix = main ?? options.modelViewProjectionMatrix;
    const posScale = main ? 1 : 512 * Math.pow(2, this.map?.getZoom() ?? 0);

    gl2.useProgram(this.program);
    gl2.uniformMatrix4fv(this.uniforms.u_matrix!, false, new Float32Array(matrix as number[]));
    gl2.uniform1f(this.uniforms.u_posScale!, posScale);
    gl2.uniform3fv(this.uniforms.u_ramp!, rampAsFloats());
    gl2.uniform3fv(this.uniforms.u_contourInk!, new Float32Array(hexToRgb(CONTOUR_INK)));
    gl2.uniform1f(this.uniforms.u_bandMinutes!, bandMinutesFor(this.style.display));
    gl2.uniform1f(this.uniforms.u_maxMinutes!, MAX_MINUTES);
    gl2.uniform1f(this.uniforms.u_opacity!, this.style.opacity);
    gl2.uniform1f(this.uniforms.u_bands!, this.style.display === 'smooth' ? 0 : 1);
    gl2.uniform1f(this.uniforms.u_contours!, this.style.contours ? 1 : 0);

    gl2.activeTexture(gl2.TEXTURE0);
    gl2.bindTexture(gl2.TEXTURE_2D, this.texture);
    gl2.uniform1i(this.uniforms.u_field!, 0);

    // MapLibre only guarantees blend and depth state; it clips raster tiles with
    // the stencil buffer, which would otherwise mask this quad away entirely.
    gl2.disable(gl2.STENCIL_TEST);
    gl2.disable(gl2.SCISSOR_TEST);
    gl2.disable(gl2.CULL_FACE);
    gl2.disable(gl2.DEPTH_TEST);
    gl2.enable(gl2.BLEND);
    gl2.blendFunc(gl2.ONE, gl2.ONE_MINUS_SRC_ALPHA);

    gl2.bindVertexArray(this.vao);
    gl2.drawArrays(gl2.TRIANGLE_STRIP, 0, 4);
    gl2.bindVertexArray(null);

  }
}
